import { Button, Grid, Typography, styled } from "@mui/material";
import React, { useState } from "react";
import { useMemo } from "react";
import PropertiesTable from "./PropertiesTable";
import { Link, useSearchParams } from "react-router-dom";
import { PROPERTY_STATUSES } from "../../constants/property";
import SharePdfModal from "./SharePdfModal";
import { usePropertiesData } from "../../hooks/useContext/agencyProperties";
import { ADD_PROPERTY_ROUTE } from "../../constants/routes";
import { AGENCY_PROPERTY_COLUMNS } from "./propertiesData";
import * as XLSX from "xlsx";
import { PROPERTY_IMAGES_URL } from "../../constants/url";

const propertyStatuses = [
  {
    id: 0,
    label: "Un Published",
    color: "error",
  },
  {
    id: 1,
    label: "Published",
    color: "success",
  },
  {
    id: 2,
    label: "Pending",
    color: "warning",
  },
  {
    id: 3,
    label: "Draft",
    color: "secondary",
  },
];

export default function Properties() {
  const { properties, isLoading: isFetching, isRefetch } = usePropertiesData();

  const [searchParams, setSearchParams] = useSearchParams();
  const [toShareProperties, setToShareProperties] = useState([]);

  const activePropertyStatus = parseInt(searchParams.get("status"));

  const isAllPropertiesStatusSelected = PROPERTY_STATUSES.every(
    (status) => status.id !== activePropertyStatus
  );

  // const filteredProperties = useMemo(() => {

  //   if (isAllPropertiesStatusSelected) return properties.Data;

  //   return properties.Data?.filter((property) => property.pstatus === activePropertyStatus);
  // }, [activePropertyStatus, properties, isAllPropertiesStatusSelected]);

  const onChangeActivePropertyStatus = (id) => {
    setSearchParams((prev) => {
      if (id === "all") {
        prev.delete("status");
        return new URLSearchParams({
          ...Object.fromEntries(prev),
        });
      }

      return {
        ...Object.fromEntries(prev),
        status: id,
      };
    });
  };

  const isOpenSharePdfModal = toShareProperties && toShareProperties.length > 0;

  const onCloseSharePdfModal = () => {
    setToShareProperties([]);
  };

  const onClickSharePdf = (propertyIds) => {
    setToShareProperties(propertyIds);
  };

  return (
    <div>
      {isOpenSharePdfModal && (
        <SharePdfModal
          isOpen={isOpenSharePdfModal}
          onClose={onCloseSharePdfModal}
          isFetching={isRefetch}
          properties={properties.Data.filter((property) =>
            toShareProperties.includes(property.propertyid)
          )}
        />
      )}
      <Typography variant="h2">Properties</Typography>

      <StyledTop>
        <StyledButton
          size="small"
          onClick={() => onChangeActivePropertyStatus("all")}
          variant={isAllPropertiesStatusSelected ? "contained" : "outlined"}
          sx={{
            color: isAllPropertiesStatusSelected ? "white" : "#56C662",
          }}
        >
          All Properties
        </StyledButton>
        {propertyStatuses.map((button, index) => (
          <StyledButton
            size="small"
            key={index}
            color={button.color}
            onClick={() => onChangeActivePropertyStatus(button.id)}
            variant={
              activePropertyStatus === button.id ? "contained" : "outlined"
            }
          >
            {button.label}
          </StyledButton>
        ))}

        <StyledButton
          size="small"
          component={Link}
          to={ADD_PROPERTY_ROUTE}
          variant="contained"
          sx={{ ml: "auto", backgroundColor: "#56C662", color: "white" }}
        >
          Add Property
        </StyledButton>
        <Grid item>
          <Button
            variant="contained"
            size="small"
            sx={{ color: "white" }}
            onClick={() => {
              if (!properties?.Data?.length) {
                alert("No property data available to export!");
                return;
              }

              // ✅ Step 1: Get columns excluding unwanted ones
              let columns = AGENCY_PROPERTY_COLUMNS.filter(
                (col) =>
                  col.accessorKey &&
                  !["actions", "edit", "view"].includes(col.accessorKey)
              );

              // ✅ Step 1.5: Add property image column at the beginning if not present
              const hasImage = columns.some(
                (col) => col.accessorKey === "FirstImage"
              );
              if (!hasImage) {
                columns.unshift({
                  accessorKey: "FirstImage",
                  header: "Property Image",
                });
              }

              // ✅ Step 2: Build export-ready array
              const exportData = properties.Data.map((property) => {
                const row = {};
                columns.forEach((col) => {
                  const key = col.accessorKey;
                  const header = col.header;
                  let value = property[key];

                  // ✅ Handle property image (FirstImage)
                  if (key === "FirstImage") {
                    value = property.FirstImage
                      ? `${PROPERTY_IMAGES_URL}${property.FirstImage}`
                      : "";
                  }

                  // ✅ Format date fields
                  if (
                    key === "createdAt" ||
                    key === "updatedAt" ||
                    key === "lastUpdated"
                  ) {
                    value = value ? new Date(value).toLocaleString() : "";
                  }

                  // ✅ Format price
                  if (key === "propertyprice") {
                    value = property.propertyprice
                      ? `₨ ${property.propertyprice}`
                      : "";
                  }

                  // ✅ Format status
                  if (key === "pstatus") {
                    value = property.statusName || property.pstatus || "";
                  }

                  // ✅ Description fallback
                  if (key === "description") {
                    value = property.description || "";
                  }

                  row[header] = value ?? "";
                });
                return row;
              });

              // ✅ Step 3: Convert to worksheet
              const worksheet = XLSX.utils.json_to_sheet(exportData);

              // ✅ Step 3.5: Bold header row
              const range = XLSX.utils.decode_range(worksheet["!ref"]);
              for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!worksheet[cellAddress]) continue;
                worksheet[cellAddress].s = { font: { bold: true } };
              }

              // ✅ Step 4: Auto column widths
              const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
                wch: Math.max(key.length + 5, 15),
              }));
              worksheet["!cols"] = colWidths;

              // ✅ Step 5: Export Excel file
              const workbook = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(workbook, worksheet, "Properties");
              XLSX.writeFile(workbook, "AllPropertiesData.xlsx");
            }}
          >
            Export All Properties
          </Button>
        </Grid>
      </StyledTop>

      <PropertiesTable
        data={properties.Data}
        paginationData={{
          PageNumber: properties?.PageNumber,
          PageSize: properties?.PageSize,
          TotalPages: properties?.TotalPages,
          TotalRecords: properties?.TotalRecords,
        }}
        onClickSharePdf={onClickSharePdf}
        isFetching={isFetching}
        isRefetch={isRefetch}
      />
    </div>
  );
}

const StyledButton = styled(Button)(({ theme }) => ({
  marginRight: "8px",
}));

const StyledTop = styled("div")(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "16px",
  display: "flex",
  flexWrap: "wrap",
  rowGap: "16px",
  alignItems: "center",
}));
